/**
 * useOfflinePatient - Hook para operações de paciente com suporte offline
 */

import { useCallback } from 'react';
import { useOffline } from '@/contexts/OfflineContext';
import { 
  createPatientLocal, 
  updatePatientLocal, 
  checkHomonymLocal,
  getAllPatientsLocal,
  getPatientLocal 
} from '@/services/indexedDB';
import axiosInstance from '@/utils/axios';
import { toast } from 'sonner';

export function useOfflinePatient() {
  const { isOnline, isOfflineReady, refreshLocalStats } = useOffline();

  /**
   * Criar paciente - funciona online e offline
   */
  const createPatient = useCallback(async (patientData) => {
    // Verificar se IndexedDB está pronto para fallback
    const canFallbackToLocal = isOfflineReady;
    
    // Se está offline, salva localmente direto
    if (!navigator.onLine) {
      console.log('[OfflinePatient] Offline detected, saving locally');
      if (!canFallbackToLocal) {
        throw new Error('Modo offline não disponível. Aguarde o sistema inicializar.');
      }
      return await savePatientLocally(patientData);
    }

    // Se está online, tenta criar no servidor
    try {
      const response = await axiosInstance.post('/patients', patientData);
      return {
        success: true,
        patient: response.data,
        isLocal: false
      };
    } catch (error) {
      console.log('[OfflinePatient] API error:', error.message, error.code);
      
      // Se falhar por erro de rede, salva localmente
      const isNetworkError = 
        error.code === 'ERR_NETWORK' || 
        error.code === 'ECONNABORTED' ||
        error.message?.includes('Network Error') ||
        error.message?.includes('timeout') ||
        !navigator.onLine;
      
      if (isNetworkError && canFallbackToLocal) {
        console.log('[OfflinePatient] Network error, saving locally');
        return await savePatientLocally(patientData);
      }
      
      // Se não é erro de rede ou não pode salvar local, propaga o erro
      throw error;
    }
  }, [isOfflineReady]);

  /**
   * Salvar paciente localmente (IndexedDB)
   */
  const savePatientLocally = async (patientData) => {
    if (!isOfflineReady) {
      throw new Error('IndexedDB não está pronto. Tente novamente em alguns segundos.');
    }

    try {
      const localPatient = await createPatientLocal({
        nome: patientData.nome,
        data_nascimento: patientData.data_nascimento,
        telefone: patientData.telefone,
        unit_id: patientData.unit_id,
        cpf: patientData.cpf,
        cns: patientData.cns
      });

      await refreshLocalStats();

      toast.info('Paciente salvo localmente. Será sincronizado quando houver conexão.', {
        duration: 4000
      });

      return {
        success: true,
        patient: {
          id: localPatient.local_id,
          nome: localPatient.nome_original,
          data_nascimento: localPatient.data_nascimento,
          telefone: localPatient.telefone,
          unit_id: localPatient.unit_id,
          data_cadastro: localPatient.created_at,
          isLocal: true,
          sync_status: localPatient.sync_status
        },
        isLocal: true
      };
    } catch (error) {
      console.error('[OfflinePatient] Error saving locally:', error);
      throw error;
    }
  };

  /**
   * Verificar homônimos - funciona online e offline
   */
  const checkHomonym = useCallback(async (nome) => {
    if (!nome || nome.length < 3) {
      return { has_homonym: false, matches: [] };
    }

    // Se online, verifica no servidor
    if (isOnline) {
      try {
        const response = await axiosInstance.get('/patients/check-homonym', {
          params: { nome }
        });
        return response.data;
      } catch (error) {
        // Se falhar, verifica localmente
        if (!navigator.onLine || error.code === 'ERR_NETWORK') {
          return await checkHomonymOffline(nome);
        }
        throw error;
      }
    }

    // Se offline, verifica localmente
    return await checkHomonymOffline(nome);
  }, [isOnline]);

  /**
   * Verificar homônimos localmente
   */
  const checkHomonymOffline = async (nome) => {
    try {
      const localPatients = await getAllPatientsLocal();
      const normalizedSearch = normalizeNameLocal(nome);
      
      const matches = localPatients.filter(p => {
        const normalizedName = p.nome_normalizado || normalizeNameLocal(p.nome_original || p.nome);
        return normalizedName === normalizedSearch;
      });

      return {
        has_homonym: matches.length > 0,
        matches: matches.map(p => ({
          id: p.local_id || p.server_id,
          nome: p.nome_original || p.nome,
          data_nascimento: p.data_nascimento,
          unit_id: p.unit_id,
          isLocal: !p.server_id
        }))
      };
    } catch (error) {
      console.error('[OfflinePatient] Error checking homonym locally:', error);
      return { has_homonym: false, matches: [] };
    }
  };

  /**
   * Atualizar paciente - funciona online e offline
   */
  const updatePatient = useCallback(async (patientId, updateData) => {
    // Se é um ID local, atualiza localmente
    if (patientId.startsWith('local_')) {
      return await updatePatientLocally(patientId, updateData);
    }

    // Se online, atualiza no servidor
    if (isOnline) {
      try {
        const response = await axiosInstance.put(`/patients/${patientId}`, updateData);
        return {
          success: true,
          patient: response.data,
          isLocal: false
        };
      } catch (error) {
        if (!navigator.onLine || error.code === 'ERR_NETWORK') {
          // Criar versão local para sync posterior
          return await updatePatientLocally(patientId, updateData);
        }
        throw error;
      }
    }

    // Offline - salvar atualização localmente
    return await updatePatientLocally(patientId, updateData);
  }, [isOnline]);

  /**
   * Atualizar paciente localmente
   */
  const updatePatientLocally = async (localId, updateData) => {
    try {
      const updated = await updatePatientLocal(localId, {
        nome_original: updateData.nome,
        data_nascimento: updateData.data_nascimento,
        telefone: updateData.telefone,
        unit_id: updateData.unit_id
      });

      await refreshLocalStats();

      toast.info('Alterações salvas localmente. Serão sincronizadas quando houver conexão.');

      return {
        success: true,
        patient: updated,
        isLocal: true
      };
    } catch (error) {
      console.error('[OfflinePatient] Error updating locally:', error);
      throw error;
    }
  };

  /**
   * Obter paciente por ID (local ou servidor)
   */
  const getPatient = useCallback(async (patientId) => {
    if (patientId.startsWith('local_')) {
      const patient = await getPatientLocal(patientId);
      return patient ? { ...patient, isLocal: true } : null;
    }

    if (isOnline) {
      try {
        const response = await axiosInstance.get(`/patients/${patientId}`);
        return { ...response.data, isLocal: false };
      } catch (error) {
        console.error('[OfflinePatient] Error getting patient:', error);
        return null;
      }
    }

    return null;
  }, [isOnline]);

  return {
    createPatient,
    updatePatient,
    checkHomonym,
    getPatient,
    isOnline,
    isOfflineReady
  };
}

/**
 * Normalizar nome localmente (equivalente ao backend)
 */
function normalizeNameLocal(name) {
  if (!name) return '';
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ç/gi, 'c')
    .toLowerCase()
    .trim();
}

export default useOfflinePatient;
